import React, { useState } from 'react';
import Card, { CardRow } from 'components/styleguide/card/Card';
import Sheet from 'components/styleguide/sheet_modal/Sheet';
import SkillsSearchInput, { Skill } from 'components/styleguide/search_input/variants/SkillsSearchInput';
import { useEditableSheet } from 'components/styleguide/sheet_modal/useEditableSheet';
const styles = require('../onboarding-cards.module.scss');

interface SkillsCardProps {
  skills: Skill[];
  url: string;
  required?: boolean;
}

const SkillsCard: React.FC<SkillsCardProps> = ({ skills: initialSkills, url, required = false }) => {
  const [skills, setSkills] = useState<Skill[]>(initialSkills || []);
  const [pendingSkills, setPendingSkills] = useState<Skill[]>([]);

  const sheet = useEditableSheet({
    url: url,
    payloadKey: 'talent',
    onSuccess: () => {
      setSkills(pendingSkills);
    },
  });

  const hasAny = skills.length > 0;
  const tag = hasAny
    ? undefined
    : required
      ? { variant: 'error' as const, icon: 'circleAlert' as const, children: 'Mandatory' }
      : { variant: 'warning' as const, icon: 'circleAlert' as const, children: 'Missing' };

  const handleAddClick = () => {
    sheet.open('Add skills', {});
  };

  const handleEditClick = () => {
    setPendingSkills(skills);
    sheet.open('Edit skills', {
      skill_ids: skills.map(s => s.value),
    });
  };

  const handleSkillsChange = (updated: Skill[]) => {
    setPendingSkills(updated);
    sheet.handleFormChange('skill_ids', updated.map(s => s.value));
  };

  return (
    <>
      <Card
        title="Skills"
        iconName="brain"
        context="talent"
        tag={tag}
        onEdit={handleEditClick}
        onAdd={hasAny ? undefined : handleAddClick}
      >
        {hasAny && (
          <CardRow context="talent">
            <p className={styles.value}>
              {skills.map(s => s.label).join(', ')}
            </p>
          </CardRow>
        )}
      </Card>

      {sheet.isOpen && (
        <Sheet
          title={sheet.sheetTitle}
          onClose={sheet.close}
          context="talent"
          showCancelButton={false}
          continueButtonText="Save"
          onContinue={sheet.handleSave}
        >
          <SkillsSearchInput
            initialSkills={skills}
            required={true}
            error={sheet.getError('skill_ids')}
            onChange={handleSkillsChange}
          />
        </Sheet>
      )}
    </>
  );
};

export default SkillsCard;
